<template>
  <section
    v-if="visible"
    class="weekly-settlement"
    aria-label="周结概览"
  >
    <div class="weekly-settlement__title">
      <strong>周结概览</strong>
      <el-button
        v-if="hasRuleText"
        class="rule-button"
        type="text"
        size="mini"
        @click="openRuleDialog"
      >
        结算规则<i class="el-icon-arrow-right el-icon--right"></i>
      </el-button>
    </div>

    <dl class="weekly-settlement__metrics">
      <div class="weekly-metric">
        <dt>结算时间</dt>
        <dd>{{ overview.settlementDate || '--' }}</dd>
      </div>
      <div class="weekly-metric weekly-metric--period">
        <dt>结算周期</dt>
        <dd :title="settlementPeriodText">{{ settlementPeriodText }}</dd>
      </div>
      <div class="weekly-metric weekly-metric--amount">
        <dt>本期结算</dt>
        <dd><span class="currency">¥</span>{{ settlementAmountText }}</dd>
      </div>
      <div class="weekly-metric weekly-metric--achievement">
        <dt>达标率</dt>
        <dd>
          <el-tag
            class="rate-tag"
            :type="achievementTagType"
            size="mini"
          >
            {{ achievementRateText }}%
          </el-tag>
          <span
            v-if="promptText"
            class="metric-note"
            :title="promptText"
          >
            {{ promptText }}
          </span>
        </dd>
      </div>
      <div class="weekly-metric weekly-metric--refund">
        <dt>退款率</dt>
        <dd>
          <el-tag
            class="rate-tag"
            :type="refundTagType"
            size="mini"
          >
            <i v-if="refundRateExceeded" class="el-icon-warning-outline"></i>
            {{ refundRateText }}%
          </el-tag>
          <span class="refund-threshold">预警值 {{ refundRateThresholdText }}</span>
        </dd>
      </div>
    </dl>

    <el-dialog
      title="结算规则"
      width="480px"
      append-to-body
      :close-on-click-modal="false"
      :visible.sync="ruleDialogVisible"
    >
      <div class="rule-text">{{ overview.ruleText }}</div>
      <span slot="footer">
        <el-button @click="ruleDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
const OVERVIEW_ENDPOINT = 'mall/bdM2sWeeklySettlement/overview'

function toFiniteNumber(value) {
  if (value === null || value === undefined) return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function clampPercentage(value) {
  const number = toFiniteNumber(value)
  return Math.min(Math.max(number === null ? 0 : number, 0), 100)
}

function formatPercentage(value) {
  return clampPercentage(value).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')
}

export default {
  name: 'WeeklySettlementOverview',
  data() {
    return {
      overview: null,
      ruleDialogVisible: false
    }
  },
  computed: {
    visible() {
      return Boolean(this.overview && this.overview.enabled)
    },
    hasRuleText() {
      return Boolean(this.overview && this.overview.ruleText)
    },
    settlementPeriodText() {
      const period = this.overview && this.overview.settlementPeriod
      if (!period || !period.startDate || !period.endDate) return '--'
      return `${period.startDate} 至 ${period.endDate}`
    },
    settlementAmountText() {
      const amount = toFiniteNumber(this.overview && this.overview.settlementAmount)
      return (amount === null ? 0 : amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    achievementRate() {
      return clampPercentage(this.overview && this.overview.achievementRate)
    },
    achievementRateText() {
      return formatPercentage(this.overview && this.overview.achievementRate)
    },
    achievementTagType() {
      return this.achievementRate >= 100 ? 'success' : 'warning'
    },
    promptText() {
      const prompt = this.overview && this.overview.prompt
      return typeof prompt === 'string' ? prompt.trim() : ''
    },
    refundRateText() {
      return formatPercentage(this.overview && this.overview.refundRate)
    },
    refundRateThresholdText() {
      const threshold = toFiniteNumber(this.overview && this.overview.refundRateThreshold)
      return threshold === null ? '--' : `${formatPercentage(threshold)}%`
    },
    refundRateExceeded() {
      if (!this.overview) return false
      const rate = toFiniteNumber(this.overview.refundRate)
      const threshold = toFiniteNumber(this.overview.refundRateThreshold)
      return rate !== null && threshold !== null && rate >= threshold
    },
    refundTagType() {
      const threshold = toFiniteNumber(this.overview && this.overview.refundRateThreshold)
      if (threshold === null) return 'info'
      return this.refundRateExceeded ? 'danger' : 'success'
    }
  },
  created() {
    this.loadOverview()
  },
  methods: {
    loadOverview() {
      return this.$http('get', OVERVIEW_ENDPOINT, {}, false, false)
        .then(overview => {
          this.overview = overview && overview.enabled ? overview : null
        })
        .catch(() => {
          // 周结概览是独立展示模块，失败时静默隐藏，不能影响原报表功能。
          this.overview = null
          this.ruleDialogVisible = false
        })
    },
    openRuleDialog() {
      if (this.hasRuleText) this.ruleDialogVisible = true
    }
  }
}
</script>

<style scoped>
.weekly-settlement {
  display: flex;
  flex: 1 1 auto;
  align-items: stretch;
  min-width: 0;
  margin-left: 2px;
  padding-left: 18px;
  border-left: 1px solid #dfe6ef;
  color: #303133;
}

.weekly-settlement__title {
  display: flex;
  flex: 0 0 74px;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.weekly-settlement__title strong {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.rule-button {
  align-self: flex-start;
  margin-top: 4px;
  padding: 0;
  color: #409eff;
  font-size: 12px;
  font-weight: 400;
}

.weekly-settlement__metrics {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: 0.82fr 1.42fr 0.94fr 1fr 1.35fr;
  min-width: 0;
  margin: 0;
}

.weekly-metric {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  margin: 0;
  padding: 0 10px;
  border-left: 1px solid #e8ecf2;
}

.weekly-metric dt {
  overflow: hidden;
  color: #909399;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.weekly-metric dd {
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 24px;
  margin: 5px 0 0;
  overflow: hidden;
  color: #303133;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  line-height: 24px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.weekly-metric--period dd {
  font-size: 13px;
}

.weekly-metric--amount dd {
  color: #e80000;
  font-size: 20px;
  font-weight: 600;
}

.currency {
  margin-right: 2px;
  font-size: 12px;
  font-weight: 500;
}

.rate-tag {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
}

.rate-tag i {
  margin-right: 2px;
}

.metric-note,
.refund-threshold {
  min-width: 0;
  margin-left: 6px;
  overflow: hidden;
  color: #909399;
  font-size: 12px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rule-text {
  min-height: 80px;
  color: #4a4a4a;
  font-size: 14px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 1100px) {
  .weekly-settlement__metrics {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    row-gap: 12px;
  }

  .weekly-metric--refund {
    grid-column: span 2;
  }
}
</style>
